/**
 * Period Selector Sub-Component (SPRINT-15 F005)
 *
 * Displays financial periods available for an entity (account) within a deal,
 * allowing the user to include/exclude periods and select a primary period
 * for analysis. Shows trust grade badges (credit_grade = green, insight_grade = amber).
 *
 * Used as an expandable sub-section within the Entity & Guarantor Config panel.
 *
 * States:
 * - Loading: Skeleton rows
 * - Empty: "No financial periods available for this entity"
 * - Loaded: Period rows with checkboxes and radio buttons
 */

import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Loader2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';

// ============================================================================
// TYPES
// ============================================================================

export interface PeriodSelectorProps {
  dealId: string;
  entityId: string;
  accountId: string;
}

interface PeriodSelection {
  id: string;
  dealId: string;
  financialPeriodId: string;
  periodLabel: string | null;
  periodType: string | null;
  includeInAnalysis: boolean;
  isPrimaryPeriod: boolean;
  createdAt: string;
}

interface FinancialPeriod {
  id: string;
  entityFinancialRecordId: string;
  periodLabel: string;
  periodType: string;
  dataSource: string;
  trustGrade: string;
  periodStartDate: string;
  periodEndDate: string;
  periodSequence: number;
}

interface EntityFinancialRecord {
  id: string;
  accountId: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const PERIOD_TYPE_LABELS: Record<string, string> = {
  historical: 'Historical',
  ytd: 'Year to Date',
  forecast: 'Forecast',
};

const DATA_SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  accounting_xero: 'Xero',
  accounting_myob: 'MYOB',
  bank_feed: 'Bank Feed',
  ai_extracted: 'AI Extracted',
  document_parsed: 'Document',
};

// ============================================================================
// COMPONENT
// ============================================================================

export const PeriodSelector: React.FC<PeriodSelectorProps> = ({ dealId, entityId, accountId }) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // ---------------------------------------------------------------------------
  // Data fetching
  // ---------------------------------------------------------------------------

  // Fetch deal period selections to know which are already selected
  const { data: periodSelections = [], isLoading: selectionsLoading } = useQuery<PeriodSelection[]>({
    queryKey: [`/api/financial/deals/${dealId}/period-selections`],
    enabled: !!dealId,
  });

  // Fetch entity financial records for this account to get available periods
  // API returns { data: EntityFinancialRecord[] }
  const { data: entityRecordsResponse } = useQuery<{ data: EntityFinancialRecord[] }>({
    queryKey: ['/api/financial/entity-records', { account_id: accountId }],
    enabled: !!accountId,
  });
  const entityRecords = entityRecordsResponse?.data ?? [];

  // Fetch available periods for this account via entity financial records
  // We use the first entity record for this account
  const firstRecordId = entityRecords.length > 0 ? entityRecords[0].id : null;

  // API returns { data: FinancialPeriod[] }
  const { data: periodsResponse, isLoading: periodsLoading } = useQuery<{ data: FinancialPeriod[] }>({
    queryKey: ['/api/financial/periods', { entity_record_id: firstRecordId }],
    enabled: !!firstRecordId,
  });
  const availablePeriods = periodsResponse?.data ?? [];

  const isLoading = selectionsLoading || periodsLoading;

  // ---------------------------------------------------------------------------
  // Map period selections by financialPeriodId for quick lookup
  // ---------------------------------------------------------------------------
  const selectionMap = new Map(
    periodSelections.map((s) => [s.financialPeriodId, s])
  );

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  // Add a period selection
  const addSelectionMutation = useMutation({
    mutationFn: async (data: { financialPeriodId: string; includeInAnalysis: boolean; isPrimaryPeriod: boolean }) => {
      const res = await apiRequest('POST', `/api/financial/deals/${dealId}/period-selections`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/financial/deals'] });
      toast({ title: 'Period added', description: 'Period has been selected for analysis' });
    },
    onError: (error: Error) => {
      if (error.message.includes('409')) {
        toast({ title: 'Already selected', description: 'Period already selected for this deal', variant: 'destructive' });
      } else {
        toast({ title: 'Error', description: error.message || 'Failed to add period', variant: 'destructive' });
      }
    },
  });

  // Update a period selection (include/exclude or primary toggle)
  const updateSelectionMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<{ includeInAnalysis: boolean; isPrimaryPeriod: boolean }> }) => {
      const res = await apiRequest('PATCH', `/api/financial/deals/${dealId}/period-selections/${id}`, data);
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/financial/deals'] });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to update period selection', variant: 'destructive' });
    },
  });

  // Remove a period selection
  const removeSelectionMutation = useMutation({
    mutationFn: async (id: string) => {
      await apiRequest('DELETE', `/api/financial/deals/${dealId}/period-selections/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/financial/deals'] });
      toast({ title: 'Period removed', description: 'Period has been deselected' });
    },
    onError: (error: Error) => {
      toast({ title: 'Error', description: error.message || 'Failed to remove period', variant: 'destructive' });
    },
  });

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleToggleInclude = (period: FinancialPeriod) => {
    const existing = selectionMap.get(period.id);

    if (existing) {
      if (existing.includeInAnalysis) {
        // Uncheck: toggle include off
        updateSelectionMutation.mutate({ id: existing.id, data: { includeInAnalysis: false } });
      } else {
        // Re-check: toggle include back on
        updateSelectionMutation.mutate({ id: existing.id, data: { includeInAnalysis: true } });
      }
    } else {
      // Not yet selected — create a new selection
      addSelectionMutation.mutate({
        financialPeriodId: period.id,
        includeInAnalysis: true,
        isPrimaryPeriod: false,
      });
    }
  };

  const handleSetPrimary = (period: FinancialPeriod) => {
    const existing = selectionMap.get(period.id);

    if (existing) {
      // Already selected — set as primary (server clears other primaries)
      updateSelectionMutation.mutate({ id: existing.id, data: { isPrimaryPeriod: true, includeInAnalysis: true } });
    } else {
      // Not yet selected — create as primary
      addSelectionMutation.mutate({
        financialPeriodId: period.id,
        includeInAnalysis: true,
        isPrimaryPeriod: true,
      });
    }
  };

  // ---------------------------------------------------------------------------
  // Trust grade badge helper
  // ---------------------------------------------------------------------------
  const getTrustGradeBadge = (trustGrade: string) => {
    if (trustGrade === 'credit_grade') {
      return (
        <Badge
          variant="outline"
          className="bg-green-50 text-green-700 border-green-200"
          data-testid="trust-grade-credit"
        >
          Credit Grade
        </Badge>
      );
    }
    if (trustGrade === 'insight_grade') {
      return (
        <Badge
          variant="outline"
          className="bg-amber-50 text-amber-700 border-amber-200"
          data-testid="trust-grade-insight"
        >
          Insight Grade
        </Badge>
      );
    }
    return null;
  };

  // ---------------------------------------------------------------------------
  // Loading
  // ---------------------------------------------------------------------------
  if (isLoading) {
    return (
      <div className="space-y-2 py-2" data-testid="period-selector-loading">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Empty
  // ---------------------------------------------------------------------------
  if (availablePeriods.length === 0) {
    return (
      <div className="py-3 text-center" data-testid="period-selector-empty">
        <p className="text-xs text-muted-foreground">
          No financial periods available for this entity
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Add financial data to the entity first via the Financials tab
        </p>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render period rows
  // ---------------------------------------------------------------------------
  const isMutating =
    addSelectionMutation.isPending ||
    updateSelectionMutation.isPending ||
    removeSelectionMutation.isPending;

  return (
    <div className="space-y-2" data-testid={`period-selector-${entityId}`}>
      <div className="flex items-center justify-between">
        <h4 className="text-xs font-medium text-muted-foreground">Period Selection</h4>
        {isMutating && <Loader2 className="h-3 w-3 animate-spin text-muted-foreground" />}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b">
              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Period</th>
              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Type</th>
              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Source</th>
              <th className="text-left py-1.5 px-2 font-medium text-muted-foreground">Grade</th>
              <th className="text-center py-1.5 px-2 font-medium text-muted-foreground">Include</th>
              <th className="text-center py-1.5 px-2 font-medium text-muted-foreground">Primary</th>
            </tr>
          </thead>
          <tbody>
            {availablePeriods.map((period) => {
              const selection = selectionMap.get(period.id);
              const isIncluded = selection?.includeInAnalysis ?? false;
              const isPrimary = selection?.isPrimaryPeriod ?? false;

              return (
                <tr
                  key={period.id}
                  className={`border-b ${!isIncluded ? 'opacity-50' : ''}`}
                  data-testid={`period-row-${period.id}`}
                >
                  <td className="py-1.5 px-2 font-medium">
                    {period.periodLabel}
                  </td>
                  <td className="py-1.5 px-2">
                    <Badge variant="secondary" className="text-[10px]">
                      {PERIOD_TYPE_LABELS[period.periodType] || period.periodType}
                    </Badge>
                  </td>
                  <td className="py-1.5 px-2">
                    <Badge variant="outline" className="text-[10px]">
                      {DATA_SOURCE_LABELS[period.dataSource] || period.dataSource}
                    </Badge>
                  </td>
                  <td className="py-1.5 px-2">
                    {getTrustGradeBadge(period.trustGrade)}
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <input
                      type="checkbox"
                      checked={isIncluded}
                      onChange={() => handleToggleInclude(period)}
                      className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      aria-label={`Include ${period.periodLabel}`}
                      data-testid={`period-include-${period.id}`}
                    />
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    <input
                      type="radio"
                      name={`primary-period-${entityId}`}
                      checked={isPrimary}
                      onChange={() => handleSetPrimary(period)}
                      className="h-4 w-4 border-gray-300 text-primary focus:ring-primary"
                      aria-label={`Set ${period.periodLabel} as primary`}
                      data-testid={`period-primary-${period.id}`}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PeriodSelector;
